import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { getFilesByMuestraId } from "../../api/Muestras/muestraApi";
import { getFileContent } from "../../api/Design/fileApi";
import {
  getCachedArchivos,
  setCachedArchivos,
} from "../../utils/cacheArchivos";
import { pickFilesForMuestraExportPhoto } from "../../utils/muestraFileCategories";
import { selectMuestrasForExcel } from "./muestrasExcelSelection";

export {
  rowInPeriod,
  selectMuestrasForExcel,
  sortMuestrasForExport,
} from "./muestrasExcelSelection";

const HEADERS = [
  "Season",
  "Brand",
  "Photo",
  "Samples",
  "Size",
  "Color",
  "Order Date",
  "Shipping Date",
  "Arrival Date",
  "Supplier",
  "Status",
  "Responsable",
  "Priority",
  "Development",
  "Courier Nº",
  "Factory Expected Date",
  "Calidades",
  "Calidades Status",
  "Print",
  "Print Status",
];

const PHOTO_COL = 2;
const IMAGE_FETCH_CONCURRENCY = 6;

function toDate(value) {
  if (value == null || value === "") return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

export function endOfDay(d) {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

function formatExcelDate(value) {
  const x = toDate(value);
  if (!x) return "";
  return x.toLocaleDateString("es-ES");
}

function mimeToExtension(mimeType) {
  if (!mimeType) return "png";
  if (mimeType.includes("jpeg") || mimeType.includes("jpg")) return "jpeg";
  if (mimeType.includes("png")) return "png";
  if (mimeType.includes("gif")) return "gif";
  return "png";
}

async function listFilesForMuestra(muestraId) {
  // Siempre refetch en export: la caché del grid puede no traer ficha técnica
  try {
    const data = await getFilesByMuestraId(muestraId);
    const files = Array.isArray(data) ? data : [];
    setCachedArchivos(muestraId, files);
    return pickFilesForMuestraExportPhoto(files);
  } catch (err) {
    // Muchas muestras no tienen carpeta de archivos → el API responde 404
    const status = err?.response?.status;
    if (status && status !== 404) {
      console.warn("Error listando archivos de muestra", muestraId, err);
    }

    const cached = getCachedArchivos(muestraId);
    if (Array.isArray(cached) && cached.length > 0) {
      return pickFilesForMuestraExportPhoto(cached);
    }

    setCachedArchivos(muestraId, []);
    return [];
  }
}

async function fetchFirstImageForMuestra(muestraId) {
  if (!muestraId) return null;

  try {
    const files = await listFilesForMuestra(muestraId);
    for (let i = 0; i < files.length; i += 1) {
      const file = files[i];
      if (!file?.path) continue;
      try {
        const response = await getFileContent(file.path);
        const { mimeType, data } = response?.data || {};
        if (mimeType?.includes("image") && data) {
          return { data, extension: mimeToExtension(mimeType) };
        }
      } catch (err) {
        console.warn("No se pudo cargar imagen de muestra", muestraId, err);
      }
    }
  } catch (err) {
    console.warn("Error obteniendo foto para exportación", muestraId, err);
  }
  return null;
}

async function mapWithConcurrency(items, concurrency, fn) {
  const results = new Array(items.length);
  let nextIndex = 0;

  async function worker() {
    while (nextIndex < items.length) {
      const i = nextIndex;
      nextIndex += 1;
      results[i] = await fn(items[i], i);
    }
  }

  const workers = Math.min(concurrency, items.length);
  await Promise.all(Array.from({ length: workers }, () => worker()));
  return results;
}

function buildDataRow(m) {
  return [
    m.TEMPORADA || "",
    m.MARCA || m.NOMBRE_CLIENTE || "",
    "",
    m.MUESTRA || "",
    m.TALLA_NOMBRE || "",
    m.COLOR_PANTONE || "",
    formatExcelDate(m.FECHA_PETICION),
    formatExcelDate(m.FECHA_ENVIO),
    formatExcelDate(m.FECHA_RECEPCION),
    m.NOMBRE_PROVEEDOR || "",
    m.ESTADO_NOMBRE || "",
    m.RESPONSABLE_SUPPLIER || "",
    m.PRIORIDAD || "",
    m.DEVELOPMENT || "",
    Array.isArray(m.COURIERS) && m.COURIERS.length
      ? m.COURIERS.join(", ")
      : m.NUM_COURIER || "",
    formatExcelDate(m.FECHA_PREVISTA_FABRICA),
    m.CALIDAD_NOMBRE || "",
    m.CALIDAD_ESTADO || "",
    m.PRINT_NOMBRE || "",
    m.PRINT_ESTADO || "",
  ];
}

/**
 * @param {object} options
 * @param {Array} options.muestras
 * @param {Date} options.rangeStart
 * @param {Date} options.rangeEnd
 * @param {boolean} [options.ignorePeriod]
 * @param {string} options.filename
 * @param {(progress: { current: number, total: number }) => void} [options.onProgress]
 */
export async function exportMuestrasToExcel({
  muestras,
  rangeStart,
  rangeEnd,
  ignorePeriod = false,
  filename,
  onProgress,
}) {
  const filtered = selectMuestrasForExcel(muestras, {
    rangeStart,
    rangeEnd,
    ignorePeriod,
  });

  const total = filtered.length;
  onProgress?.({ current: 0, total });

  const images = await mapWithConcurrency(
    filtered,
    IMAGE_FETCH_CONCURRENCY,
    async (m, index) => {
      const muestraId = m.id ?? m.ID_MUESTRA;
      let image = null;
      try {
        image = await fetchFirstImageForMuestra(muestraId);
      } catch (err) {
        console.warn("Exportación: fila omitida por error", muestraId, err);
      }
      onProgress?.({ current: index + 1, total });
      return image;
    },
  );

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Muestras");

  worksheet.columns = [
    { width: 14 }, // Season
    { width: 20 }, // Brand
    { width: 16 }, // Photo
    { width: 24 }, // Samples
    { width: 10 }, // Size
    { width: 18 }, // Color
    { width: 13 }, // Order Date
    { width: 13 }, // Shipping Date
    { width: 13 }, // Arrival Date
    { width: 22 }, // Supplier
    { width: 16 }, // Status
    { width: 16 }, // Responsable
    { width: 12 }, // Priority
    { width: 18 }, // Development
    { width: 14 }, // Courier
    { width: 16 }, // Factory Expected Date
    { width: 18 }, // Calidades
    { width: 14 }, // Calidades Status
    { width: 18 }, // Print
    { width: 14 }, // Print Status
  ];

  const headerRow = worksheet.addRow(HEADERS);
  headerRow.font = { bold: true };
  headerRow.alignment = { vertical: "middle", horizontal: "center" };
  headerRow.height = 22;

  filtered.forEach((m, index) => {
    const row = worksheet.addRow(buildDataRow(m));
    row.height = 88;
    row.alignment = { vertical: "middle", wrapText: true };

    const image = images[index];
    if (!image?.data) return;

    try {
      const imageId = workbook.addImage({
        base64: image.data,
        extension: image.extension,
      });

      const excelRow = row.number;
      worksheet.addImage(imageId, {
        tl: { col: PHOTO_COL + 0.12, row: excelRow - 1 + 0.08 },
        ext: { width: 96, height: 100 },
      });
    } catch (err) {
      console.warn(
        "No se pudo incrustar imagen en Excel",
        m.id ?? m.ID_MUESTRA,
        err,
      );
    }
  });

  worksheet.views = [{ state: "frozen", ySplit: 1 }];

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, filename);
}
